/**
 * Navigation is derived from permissions, not from role names.
 *
 * Deriving from roles would break the moment a school customises one — and
 * customising roles is a product feature. Each item declares the permission
 * that makes it meaningful, and the item disappears for anyone who lacks it.
 * docs/07-frontend.md §6
 */

import type { PortalId } from "@/shared/api/types";
import type { Permission } from "@/shared/auth/permissions";

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: string;
  /** Hidden unless the user holds this permission. */
  permission?: Permission;
  /**
   * Hidden unless the user holds at least one of these.
   *
   * Needed wherever an endpoint accepts either a broad permission or its
   * `.self` variant. A student holds `attendance.view.self`, never
   * `attendance.view`, so a single-permission gate hides their own attendance
   * from them while the API would happily serve it.
   */
  anyOf?: Permission[];
  /**
   * Set when the destination does not exist yet.
   *
   * Hidden entirely in deployed environments: "Phase 3" is our project plan,
   * and a teacher at a pilot school has no idea what it means. Shown greyed in
   * local development so the team can see what is still outstanding without
   * reading the roadmap.
   */
  phase?: string;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export const PORTAL_LABELS: Record<PortalId, string> = {
  admin: "Administration",
  teacher: "Teaching",
  parent: "Family",
};

const ADMIN_NAV: NavSection[] = [
  {
    id: "overview",
    label: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", to: "/admin", icon: "▤" },
      {
        id: "school",
        label: "School profile",
        to: "/admin/school",
        icon: "◫",
        permission: "school.view",
      },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      {
        id: "people-users",
        label: "Users & access",
        to: "/admin/users",
        icon: "◈",
        permission: "user.view",
      },
      {
        // Before Students, because that is the order they happen in: a child is
        // an applicant before they are on the roster.
        id: "admissions",
        label: "Admissions",
        to: "/admin/admissions",
        icon: "◑",
        permission: "admission.view",
      },
      {
        id: "students",
        label: "Students",
        to: "/admin/students",
        icon: "◉",
        permission: "student.view",
      },
      {
        id: "imports",
        label: "Import students",
        to: "/admin/imports",
        icon: "⇪",
        permission: "student.create",
      },
      {
        id: "guardians",
        label: "Guardians",
        to: "/admin/guardians",
        icon: "◎",
        permission: "guardian.view",
      },
      {
        id: "staff",
        label: "Staff",
        to: "/admin/staff",
        icon: "◈",
        permission: "staff.view",
      },
    ],
  },
  {
    id: "academics",
    label: "Academics",
    items: [
      {
        id: "academics",
        label: "Classes & sections",
        to: "/admin/academics",
        icon: "▦",
        permission: "class.view",
      },
      {
        id: "attendance",
        label: "Attendance",
        to: "/admin/attendance",
        icon: "✓",
        permission: "attendance.view",
      },
      {
        id: "timetable",
        label: "Timetable",
        to: "/admin/timetable",
        icon: "▥",
        permission: "timetable.view",
      },
      {
        // Beside Examinations, because to a school these are the same shelf —
        // one is the paper the school set and the other is the paper somebody
        // else set, and the child sat both.
        // Beside Competitions and Examinations: the same shelf to a school —
        // the paper the school set, the paper somebody else set, and the paper
        // the child sits to get ready for both.
        id: "practice",
        label: "Practice",
        to: "/admin/practice",
        icon: "◇",
        permission: "practice.view",
      },
      {
        id: "competitions",
        label: "Competitions",
        to: "/admin/competitions",
        icon: "◆",
        permission: "competition.view",
      },
      {
        id: "exams",
        label: "Examinations",
        to: "/admin/exams",
        icon: "◍",
        permission: "exam.view",
      },
      {
        // Its own section would be one line long, and transport is a thing a
        // school runs alongside the school day rather than beside it.
        id: "transport",
        label: "Transport",
        to: "/admin/transport",
        icon: "◈",
        permission: "transport.view",
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      {
        id: "fees",
        label: "Fees",
        to: "/admin/fees",
        icon: "₹",
        permission: "fees.view",
      },
      {
        id: "fee-structures",
        label: "Fee structures",
        to: "/admin/fees/structures",
        icon: "◐",
        permission: "fees.manage",
      },
      {
        // Under Finance rather than People: the register is a month of money,
        // and the person who opens it is the one who opens the fee day sheet.
        // What an individual is paid lives on their staff profile instead.
        id: "payroll",
        label: "Salary register",
        to: "/admin/payroll",
        icon: "▤",
        permission: "payroll.view",
      },
    ],
  },
  {
    // One item, for the many people who hold no other payroll right. Kept out
    // of Finance so a teacher's sidebar does not grow a Finance heading.
    id: "personal",
    label: "You",
    items: [
      {
        id: "my-payslips",
        label: "My payslips",
        to: "/admin/my-payslips",
        icon: "◱",
        permission: "payroll.view.self",
      },
    ],
  },
  {
    id: "ai",
    label: "AI",
    items: [
      {
        id: "ai-assistant",
        label: "Ask about your school",
        to: "/admin/ai",
        icon: "✦",
        permission: "ai.use",
      },
      {
        id: "ai-suggestions",
        label: "AI suggestions",
        to: "/admin/ai/suggestions",
        icon: "◈",
        permission: "ai.proposal.view",
      },
      {
        id: "ai-settings",
        label: "AI settings",
        to: "/admin/ai/settings",
        icon: "◇",
        permission: "ai.manage",
      },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [
      { id: "status", label: "Platform status", to: "/admin/status", icon: "◇" },
      {
        id: "announcements",
        label: "Noticeboard",
        to: "/admin/announcements",
        icon: "◧",
        permission: "announcement.view",
      },
      {
        id: "notifications",
        label: "Notifications",
        to: "/admin/notifications",
        icon: "◔",
        permission: "notification.view.self",
      },
      {
        id: "deliveries",
        label: "Message delivery",
        to: "/admin/notifications/deliveries",
        icon: "◑",
        permission: "notification.broadcast",
      },
      {
        id: "audit",
        label: "Audit trail",
        to: "/admin/audit",
        icon: "▥",
        permission: "audit.view",
      },
    ],
  },
];

const TEACHER_NAV: NavSection[] = [
  {
    id: "today",
    label: "Today",
    items: [
      { id: "dashboard", label: "Dashboard", to: "/teacher", icon: "▤" },
      {
        id: "attendance",
        label: "Mark attendance",
        to: "/teacher/attendance",
        icon: "✓",
        permission: "attendance.create",
      },
    ],
  },
  {
    id: "classes",
    label: "My classes",
    items: [
      {
        id: "sections",
        label: "My sections",
        to: "/teacher/sections",
        icon: "▦",
        permission: "class.view",
      },
      {
        id: "marks",
        label: "Marks entry",
        to: "/teacher/marks",
        icon: "◍",
        permission: "marks.create",
      },
      {
        // The same omission as the payslip below, and for the same reason. A
        // teacher holds `practice.approve` — reading a generated question with
        // its answer key and vouching for it is the whole point of the gate —
        // but the only link to that screen was in the administration portal,
        // which a teacher never opens. A permission nobody can reach is not a
        // permission.
        id: "practice",
        label: "Practice",
        to: "/teacher/practice",
        icon: "◇",
        permission: "practice.view",
      },
      {
        id: "timetable",
        label: "Timetable",
        to: "/teacher/timetable",
        icon: "▥",
        permission: "timetable.view",
      },
      {
        id: "announcements",
        label: "Noticeboard",
        to: "/teacher/announcements",
        icon: "◧",
        permission: "announcement.view",
      },
      {
        // A teacher holds `payroll.view.self` and had no way to reach it: the
        // link lived only in the admin portal, which a teacher never opens. A
        // permission nothing links to is a permission nobody uses.
        id: "my-payslips",
        label: "My payslips",
        to: "/teacher/my-payslips",
        icon: "◱",
        permission: "payroll.view.self",
      },
      {
        id: "notifications",
        label: "Notifications",
        to: "/teacher/notifications",
        icon: "◔",
        permission: "notification.view.self",
      },
      {
        id: "ai-assistant",
        label: "Teaching assistant",
        to: "/teacher/ai",
        icon: "✦",
        permission: "ai.use",
      },
      {
        id: "ai-suggestions",
        label: "AI suggestions",
        to: "/teacher/ai/suggestions",
        icon: "◈",
        permission: "ai.proposal.view",
      },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [{ id: "status", label: "Platform status", to: "/teacher/status", icon: "◇" }],
  },
];

const PARENT_NAV: NavSection[] = [
  {
    id: "family",
    label: "My family",
    items: [
      { id: "dashboard", label: "Dashboard", to: "/family", icon: "▤" },
      {
        id: "attendance",
        label: "Attendance",
        to: "/family/attendance",
        icon: "✓",
        anyOf: ["attendance.view", "attendance.view.self"],
      },
      {
        id: "results",
        label: "Results",
        to: "/family/results",
        icon: "◍",
        anyOf: ["marks.view", "marks.view.self"],
      },
      {
        // Only a student sees this: `practice.attempt` is theirs alone. A
        // parent reads the finished result on the child's record instead —
        // watching a paper being sat is watching a child think.
        id: "practice",
        label: "Practice",
        to: "/family/practice",
        icon: "◇",
        permission: "practice.attempt",
      },
      {
        id: "timetable",
        label: "Timetable",
        to: "/family/timetable",
        icon: "▥",
        permission: "timetable.view",
      },
      {
        id: "announcements",
        label: "Noticeboard",
        to: "/family/announcements",
        icon: "◧",
        permission: "announcement.view",
      },
      {
        id: "notifications",
        label: "Notifications",
        to: "/family/notifications",
        icon: "◔",
        permission: "notification.view.self",
      },
      {
        id: "fees",
        label: "Fees",
        to: "/family/fees",
        icon: "₹",
        anyOf: ["fees.view", "fees.view.self"],
      },
      {
        id: "ai-assistant",
        label: "Ask a question",
        to: "/family/ai",
        icon: "✦",
        permission: "ai.use",
      },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [{ id: "status", label: "Platform status", to: "/family/status", icon: "◇" }],
  },
];

export const PORTAL_NAV: Record<PortalId, NavSection[]> = {
  admin: ADMIN_NAV,
  teacher: TEACHER_NAV,
  parent: PARENT_NAV,
};

export const PORTAL_HOME: Record<PortalId, string> = {
  admin: "/admin",
  teacher: "/teacher",
  parent: "/family",
};
